import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Avatar,
  Skeleton,
} from "@mui/material";
import { useSnackbar } from "notistack";
import sh from "bundles/common/utils/sh";
import NotesPanel from "bundles/Internal/components/common/NotesPanel";
import CopyButton from "bundles/Internal/components/common/CopyButton";
import { ArrowLeft, Briefcase, Users } from "lucide-react";
import {
  INDUSTRY_COLOR,
  OFFER_STATUS_COLOR,
  LANGUAGE_COLOR,
} from "bundles/Internal/components/job_offer/JobOfferTable";

const DATE_KEYS = new Set(["created_at", "published_at", "refresh_at"]);
const DATE_ONLY_KEYS = new Set(["end_of_mandate"]);

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("fr-BE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

const pill = (label, c) => (
  <Chip
    label={String(label).replaceAll("_", " ")}
    size="small"
    sx={{
      backgroundColor: c.bg,
      color: c.text,
      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      fontSize: 11,
      fontWeight: 600,
      borderRadius: "8px",
      height: 24,
    }}
  />
);

const formatLocations = (locations) => {
  if (!Array.isArray(locations) || locations.length === 0) return "—";
  return locations
    .map((loc) => {
      if (loc.city) return loc.city;
      if (loc.region) return loc.region;
      if (loc.district) return loc.district;
      if (loc.country) return loc.country;
      return null;
    })
    .filter(Boolean)
    .join(", ");
};

const formatJobOfferCell = (jobOffer, key) => {
  const value = jobOffer?.[key];
  if (value === null || value === undefined || value === "") return "—";

  if (DATE_KEYS.has(key)) return formatDateTime(value);
  if (DATE_ONLY_KEYS.has(key)) return formatDate(value);

  if (key === "company_industries" && Array.isArray(value) && value.length > 0) {
    return (
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
        {value.map((ind) => (
          <span key={ind}>
            {pill(ind, INDUSTRY_COLOR[ind] || INDUSTRY_COLOR.industry)}
          </span>
        ))}
      </Box>
    );
  }

  if (key === "categories" && Array.isArray(value) && value.length > 0) {
    return value.join(", ");
  }

  if (key === "job_statuses" && Array.isArray(value) && value.length > 0) {
    return value.map(s => s.replaceAll("_", " ")).join(", ");
  }

  if (key === "offer_status" && value) {
    return pill(value, OFFER_STATUS_COLOR[value] || OFFER_STATUS_COLOR.draft);
  }

  if (key === "offer_language" && value) {
    return pill(value, LANGUAGE_COLOR[value] || LANGUAGE_COLOR.english);
  }

  if (key === "locations") {
    return formatLocations(value);
  }

  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const sectionPaperSx = {
  borderRadius: "0.75rem",
  overflow: "hidden",
  border: "1px solid hsl(140 8% 88%)",
  p: 4,
};

const sectionTitleSx = {
  color: "hsl(150 30% 12%)",
  fontWeight: 600,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  mb: 3,
  display: "flex",
  alignItems: "center",
  gap: 1,
};

const KeyValueTile = ({ label, value }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: "calc(0.75rem - 2px)",
      backgroundColor: "hsl(140 10% 95%)",
      height: "100%",
    }}
  >
    <Typography
      sx={{
        color: "hsl(150 10% 46%)",
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        fontWeight: 600,
        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        color: "hsl(150 30% 12%)",
        mt: 0.5,
        fontSize: 14,
        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
      }}
    >
      {value}
    </Box>
  </Box>
);

const tableHeadCellSx = {
  backgroundColor: "transparent",
  borderBottom: "1px solid hsl(140 8% 88%)",
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "hsl(150 10% 46%)",
  py: "10px",
  px: "16px",
  whiteSpace: "nowrap",
};

const tableBodyCellSx = {
  whiteSpace: "nowrap",
  py: "14px",
  px: "16px",
  fontSize: 12,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: "hsl(150 30% 12%)",
  maxWidth: 240,
  overflow: "hidden",
  textOverflow: "ellipsis",
  borderBottom: "1px solid hsl(140 8% 88%)",
};

const APPLICATION_STATUS_COLOR = {
  pending: { border: "#F57C00", bg: "rgba(245,124,0,0.10)", text: "#F57C00" },
  accepted: { border: "#079344", bg: "rgba(7,147,68,0.10)", text: "#079344" },
  rejected: { border: "#D32F2F", bg: "rgba(211,47,47,0.10)", text: "#D32F2F" },
  withdrawn: { border: "#757575", bg: "rgba(117,117,117,0.10)", text: "#757575" },
};

const COLUMNS_CONSTANTS = [
  { key: "id", label: "Id" },
  { key: "job_title", label: "Job Title" },
  { key: "company_name", label: "Company" },
  { key: "recruiter_name", label: "Recruiter" },
  { key: "end_of_mandate", label: "End of Mandate" },
  { key: "company_industries", label: "Industries" },
  { key: "categories", label: "Categories" },
  { key: "job_statuses", label: "Job Statuses" },
  { key: "offer_status", label: "Offer Status" },
  { key: "offer_language", label: "Language" },
  { key: "locations", label: "Locations" },
  { key: "view_count", label: "Views" },
  { key: "intent_apply_count", label: "Intent to Apply" },
  { key: "created_at", label: "Created At" },
  { key: "published_at", label: "Published At" },
  { key: "refresh_at", label: "Refresh At" },
  { key: "credit_id", label: "Credit ID" },
];

function DetailsTab({ jobOffer, loading }) {
  if (loading) {
    return (
      <Grid container spacing={3}>
        {[...Array(12)].map((_, idx) => (
          <Grid item xs={12} sm={6} key={idx}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="60%" />
          </Grid>
        ))}
      </Grid>
    );
  }
  if (!jobOffer) return null;

  return (
    <Grid container spacing={3} alignItems="stretch">
      {COLUMNS_CONSTANTS.map((col) => {
        const value = formatJobOfferCell(jobOffer, col.key);
        if (value === "—" || value === "" || value === null || value === undefined) return null;
        return (
          <Grid item xs={12} sm={6} key={col.key}>
            <KeyValueTile label={col.label} value={value} />
          </Grid>
        );
      })}
    </Grid>
  );
}

function ApplicationsTab({ applications, loading }) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <Stack spacing={1}>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={36} />
        ))}
      </Stack>
    );
  }
  if (!applications || applications.length === 0) {
    return (
      <Box sx={{ p: 8, textAlign: "center" }}>
        <Typography sx={{ color: "hsl(150 10% 46%)", fontSize: 14 }}>
          No applications received for this job offer yet.
        </Typography>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        overflowX: "auto",
        borderRadius: "8px",
        backgroundColor: "#fff",
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            {[
              "Id",
              "Candidate",
              "Email",
              "Phone",
              "Status",
              "Applied At",
              "Source",
            ].map((label) => (
              <TableCell key={label} sx={tableHeadCellSx}>{label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {applications.map((app) => {
            const statusColor = APPLICATION_STATUS_COLOR[app.status] || APPLICATION_STATUS_COLOR.pending;
            const candidate = app.candidate || {};
            const source = app.sent_from_talent_team
              ? "Talent Team"
              : app.added_from_cvtheque
                ? "CV Library"
                : "Direct";

            return (
              <TableRow
                key={app.id}
                hover
                onClick={() => navigate(`/internal/job_applications/${app.id}`)}
                sx={{
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "hsl(140 8% 90%)" },
                  "&:last-child td": { borderBottom: "none" },
                }}
              >
                <TableCell sx={tableBodyCellSx}>{app.id}</TableCell>
                <TableCell sx={tableBodyCellSx}>
                  {candidate?.id ? (
                    <Box
                      component="span"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/internal/candidates/${candidate.id}`);
                      }}
                      sx={{
                        color: "#079344",
                        fontWeight: 600,
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {[candidate.first_name, candidate.last_name].filter(Boolean).join(" ") || "—"}
                    </Box>
                  ) : "—"}
                </TableCell>
                <TableCell sx={tableBodyCellSx}>
                  {candidate.email ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0, maxWidth: 200 }}>
                      <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: 1 }}>{candidate.email}</Box>
                      <CopyButton value={candidate.email} sx={{ flexShrink: 0 }} />
                    </Box>
                  ) : "—"}
                </TableCell>
                <TableCell sx={tableBodyCellSx}>
                  {candidate.phone ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0, maxWidth: 200 }}>
                      <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: 1 }}>{candidate.phone}</Box>
                      <CopyButton value={candidate.phone} sx={{ flexShrink: 0 }} />
                    </Box>
                  ) : "—"}
                </TableCell>
                <TableCell sx={tableBodyCellSx}>
                  {app.status ? pill(app.status, statusColor) : "—"}
                </TableCell>
                <TableCell sx={tableBodyCellSx}>{formatDateTime(app.created_at) || "—"}</TableCell>
                <TableCell sx={tableBodyCellSx}>{source}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

const TAB_KEYS = ["details", "applications"];

function JobOfferShowPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const [jobOffer, setJobOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const activeTab = Math.max(TAB_KEYS.indexOf(searchParams.get("tab")), 0);
  const setActiveTab = (v) => setSearchParams({ tab: TAB_KEYS[v] }, { replace: true, state: location.state });
  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/internal/job_offers"));
  const [repostOpen, setRepostOpen] = useState(false);
  const [repostDays, setRepostDays] = useState("");
  const [reposting, setReposting] = useState(false);

  const fetchJobOffer = (withSpinner = true) => {
    if (!id) return;
    if (withSpinner) setLoading(true);
    setError(null);

    sh.get(`/internal/job_offers/${id}`)
      .then((res) => {
        setJobOffer(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err?.response?.status === 401) {
          window.location.href = "/internal/sign_in";
          return;
        }
        setError("Failed to load job offer");
      });
  };

  useEffect(() => {
    fetchJobOffer();
  }, [id]);

  const eligibility = jobOffer?.repost_eligibility;
  const maxDays = eligibility?.max_days || 0;
  const creditMode = eligibility?.credit_mode;

  const openRepost = () => {
    setRepostDays(String(Math.min(90, maxDays) || maxDays));
    setRepostOpen(true);
  };

  const repostDaysInt = parseInt(repostDays, 10);
  const previewEndOfMandate =
    repostDaysInt >= 1 ? formatDate(Date.now() + repostDaysInt * 24 * 60 * 60 * 1000) : null;

  const submitRepost = () => {
    const days = parseInt(repostDays, 10);
    if (!days || days < 1 || days > maxDays) {
      enqueueSnackbar(`Days must be between 1 and ${maxDays}`, { variant: "error" });
      return;
    }

    setReposting(true);
    sh.post(`/internal/job_offers/${id}/repost`, { days })
      .then(() => {
        enqueueSnackbar("Job offer reposted", { variant: "success" });
        setRepostOpen(false);
        fetchJobOffer(false);
      })
      .catch((err) => {
        enqueueSnackbar(err?.response?.data?.error || "Failed to repost job offer", { variant: "error" });
      })
      .finally(() => setReposting(false));
  };

  const title = useMemo(() => {
    if (!jobOffer) return "";
    return jobOffer.job_title || `Job Offer #${jobOffer.id}`;
  }, [jobOffer]);

  const getInitials = () => {
    if (!jobOffer || !jobOffer.job_title) return "JO";
    const words = jobOffer.job_title.split(" ").filter(Boolean);
    if (words.length === 0) return "JO";
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 72px)",
        backgroundColor: "hsl(140 10% 95%)",
      }}
    >
      {/* Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 30,
          backgroundColor: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(16px)",
          borderBottom: "1px solid hsl(140 8% 88%)",
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ py: 1.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ minWidth: 0 }}
            >
              <Button
                onClick={goBack}
                disableRipple
                sx={{
                  minWidth: 0,
                  p: 0.75,
                  color: "hsl(150 10% 46%)",
                  "&:hover": { color: "hsl(150 30% 12%)", backgroundColor: "transparent" },
                }}
              >
                <ArrowLeft size={20} />
              </Button>

              {loading ? (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box>
                    <Skeleton width={140} height={20} />
                    <Skeleton width={200} height={14} />
                  </Box>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      fontSize: 15,
                      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                      backgroundColor: "hsl(150 38% 17%)",
                      color: "hsl(80 97% 72%)",
                      flexShrink: 0,
                    }}
                  >
                    {getInitials()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap" }}>
                      <Typography
                        sx={{
                          fontSize: { xs: 16, sm: 18 },
                          color: "hsl(150 30% 12%)",
                          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {title}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{ mt: 0.25, flexWrap: "wrap", rowGap: 0.25 }}
                    >
                      {jobOffer?.company_name && (
                        <Typography
                          component={jobOffer?.company?.id ? RouterLink : "span"}
                          to={jobOffer?.company?.id ? `/internal/companies/${jobOffer.company.id}` : undefined}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontSize: 12,
                            color: jobOffer?.company?.id ? "#079344" : "hsl(150 10% 46%)",
                            fontWeight: jobOffer?.company?.id ? 600 : 400,
                            textDecoration: "none",
                            "&:hover": jobOffer?.company?.id ? { textDecoration: "underline" } : {},
                          }}
                        >
                          {jobOffer.company_name}
                        </Typography>
                      )}
                      {jobOffer?.offer_status && (
                        <Box>
                          {pill(
                            jobOffer.offer_status,
                            OFFER_STATUS_COLOR[jobOffer.offer_status] || OFFER_STATUS_COLOR.draft
                          )}
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </Stack>
              )}

              {!loading && jobOffer && (
                <Tooltip title={eligibility?.eligible ? "" : eligibility?.reason || ""}>
                  <span>
                    <Button
                      variant="contained"
                      disableElevation
                      disableRipple
                      disabled={!eligibility?.eligible}
                      onClick={openRepost}
                      sx={{
                        flexShrink: 0,
                        textTransform: "none",
                        height: "40px",
                        px: 1.5,
                        borderRadius: "calc(-2px + 0.75rem)",
                        backgroundColor: "hsl(150 38% 17%)",
                        color: "hsl(80 97% 72%)",
                        fontSize: 14,
                        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                        "& .MuiButton-startIcon": { mr: 0.75 },
                        transition: "opacity 0.15s ease",
                        "&:hover": { backgroundColor: "hsl(150 38% 17%)", opacity: 0.9 },
                        "&:disabled": {
                          opacity: 0.4,
                          backgroundColor: "hsl(150 38% 17%)",
                          color: "hsl(80 97% 72%)",
                        },
                      }}
                    >
                      Repost
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Stack>
          </Box>
        </Container>
      </Box>

      <Dialog
        open={repostOpen}
        onClose={() => setRepostOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", maxWidth: 440 } }}
      >
        <DialogTitle
          sx={{
            fontSize: 16,
            fontWeight: 600,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            color: "hsl(150 30% 12%)",
            pb: 0.5,
          }}
        >
          Repost job offer
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: "8px",
              backgroundColor: creditMode === "consume" ? "rgba(245,124,0,0.10)" : "rgba(7,147,68,0.10)",
              border: `1px solid ${creditMode === "consume" ? "rgba(245,124,0,0.30)" : "rgba(7,147,68,0.30)"}`,
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                color: creditMode === "consume" ? "#B45309" : "#079344",
              }}
            >
              {creditMode === "consume"
                ? "This will consume 1 available licence credit."
                : "Reuses this offer’s existing licence: free, no credit consumed."}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: 14,
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              color: "hsl(150 10% 46%)",
              mb: 2,
            }}
          >
            Extend this offer by up to {maxDays} day{maxDays === 1 ? "" : "s"} (capped at the
            credit&apos;s remaining validity).
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="number"
            label="Number of days"
            value={repostDays}
            onChange={(e) => setRepostDays(e.target.value)}
            inputProps={{ min: 1, max: maxDays }}
            helperText={`Between 1 and ${maxDays} days`}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: 14,
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                color: "hsl(150 30% 12%)",
                "&.Mui-focused fieldset": { borderColor: "hsl(150 38% 17%)", borderWidth: 2 },
              },
              "& label.Mui-focused": { color: "hsl(150 38% 17%)" },
            }}
          />
          {previewEndOfMandate && (
            <Typography
              sx={{
                mt: 1.5,
                fontSize: 13,
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                color: "hsl(150 30% 12%)",
              }}
            >
              New end of mandate: {previewEndOfMandate}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            disableRipple
            onClick={() => setRepostOpen(false)}
            sx={{
              textTransform: "none",
              fontSize: 14,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              color: "hsl(150 10% 46%)",
              borderRadius: "8px",
              height: "auto",
              px: 2,
              "&:hover": { backgroundColor: "hsl(140 8% 90%)" },
            }}
          >
            Cancel
          </Button>
          <Button
            disableRipple
            disableElevation
            variant="contained"
            disabled={reposting}
            onClick={submitRepost}
            sx={{
              textTransform: "none",
              minHeight: 0,
              height: "auto",
              fontSize: 14,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              borderRadius: "8px",
              px: 2,
              backgroundColor: "hsl(150 38% 17%)",
              color: "hsl(80 97% 72%)",
              "&:hover": { backgroundColor: "hsl(150 38% 14%)" },
              "&.Mui-disabled": { backgroundColor: "hsl(150 15% 55%)", color: "hsl(80 30% 85%)" },
            }}
          >
            {reposting ? "Reposting…" : "Repost"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Page Content */}
      <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2, md: 3 }, py: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={TAB_KEYS[activeTab] === "applications" ? 12 : 8}>
            <Box sx={{ borderBottom: "1px solid hsl(140 8% 88%)", mb: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 36,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontSize: 13,
                    minHeight: 36,
                    py: 0.5,
                    px: 1.5,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    color: "hsl(150 10% 46%)",
                    "&.Mui-selected": { color: "hsl(150 38% 17%)" },
                  },
                  "& .MuiTab-iconWrapper": { fontSize: 16 },
                  "& .MuiTabs-indicator": { backgroundColor: "hsl(150 38% 17%)" },
                }}
              >
                <Tab icon={<Briefcase size={16} />} iconPosition="start" label="Details" />
                <Tab
                  icon={<Users size={16} />}
                  iconPosition="start"
                  label={`Applications${
                    jobOffer?.job_applications?.length ? ` (${jobOffer.job_applications.length})` : ""
                  }`}
                />
              </Tabs>
            </Box>

            <Paper elevation={0} sx={{ ...sectionPaperSx, ...(TAB_KEYS[activeTab] === "applications" && { p: 0 }) }}>
              {error && (
                <Box
                  sx={{
                    p: 2,
                    m: 4,
                    backgroundColor: "hsla(0, 72%, 51%, 0.08)",
                    border: "1px solid hsla(0, 72%, 51%, 0.2)",
                    borderRadius: "calc(0.75rem - 2px)",
                  }}
                >
                  <Typography sx={{ color: "hsl(0 72% 51%)", fontSize: 14 }}>{error}</Typography>
                </Box>
              )}

              {TAB_KEYS[activeTab] === "details" && (
                <>
                  <Typography variant="h6" sx={sectionTitleSx}>
                    <Briefcase size={20} color="hsl(150 38% 17%)" />
                    Job Offer Details
                  </Typography>
                  <DetailsTab jobOffer={jobOffer} loading={loading} />
                </>
              )}

              {TAB_KEYS[activeTab] === "applications" && (
                <ApplicationsTab applications={jobOffer?.job_applications} loading={loading} />
              )}
            </Paper>
          </Grid>

          {TAB_KEYS[activeTab] !== "applications" && (
            <Grid item xs={12} lg={4}>
              <Box
                sx={{
                  position: { lg: "sticky" },
                  top: { lg: 80 },
                }}
              >
                <NotesPanel resource="job_offers" recordId={id} />
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
}

export default JobOfferShowPage;
